import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import Sortable from "sortablejs"

/**
 * Stimulus controller for making list items sortable using SortableJS.
 *
 * Supports nested sortable lists (e.g., modules containing milestones).
 * Handles the complexity of DOM node removal/reinsertion that occurs during dragging
 * by deferring sortable instance destruction until after the drag operation completes.
 *
 * @example
 * <ul data-controller="sortable"
 *     data-sortable-url-value="/api/reorder"
 *     data-sortable-handle-value=".drag-handle">
 *   <li data-sortable-id="1">Item 1</li>
 *   <li data-sortable-id="2">Item 2</li>
 * </ul>
 */
export default class extends Controller {
  /**
   * @typedef {Object} SortableValues
   * @property {string} url - Endpoint to POST the new item order to
   * @property {string} [handle=".drag-handle"] - CSS selector for the drag handle element
   * @property {number} [animation=150] - Animation duration in milliseconds
   * @property {string} [group] - Name of sortable group for multi-list interactions
   */
  static values = {
    url: String,
    handle: { type: String, default: ".drag-handle" },
    animation: { type: Number, default: 150 },
    group: String
  }

  /** @type {Sortable} The SortableJS instance attached to this element */
  sortable = null

  /** @type {number} Animation frame ID for deferred initialization */
  connectRAF = null

  /**
   * Initializes the sortable instance when the controller connects.
   *
   * If another Sortable drag is already in progress globally, defers initialization
   * to the next animation frame to avoid interfering with the active drag operation.
   * This is critical for nested sortables where dragging a parent list shouldn't
   * prevent child lists from initializing.
   */
  connect() {
    if (this.isGhost) return

    // If another Sortable is active, delay initialization to avoid interrupting the drag
    if (Sortable.active) {
      this.connectRAF = requestAnimationFrame(() => {
        this.connectRAF = requestAnimationFrame(() => this.initializeSortable())
      })
    } else {
      this.initializeSortable()
    }
  }

  /**
   * Cleans up the sortable instance when the controller disconnects.
   *
   * This is called when the DOM element is removed (e.g., when a parent list item is moved
   * during a drag operation). The key insight: we must NOT destroy the sortable instance
   * while a drag is in progress (Sortable.active === true), as this would interrupt the
   * drag and prevent the order from being persisted.
   *
   * Instead, we nullify the reference and let the next connect() create a fresh instance.
   * This solves the nested sortable problem: when a module is dragged, its DOM is removed,
   * the milestone sortable disconnects, but we don't destroy it mid-drag. When the module
   * is reinserted, the milestone sortable reconnects and reinitializes cleanly.
   */
  disconnect() {
    // Cancel any pending initialization
    if (this.connectRAF) {
      cancelAnimationFrame(this.connectRAF)
      this.connectRAF = null
    }

    // Clean up sortable instance
    if (this.sortable) {
      // If a drag is active, don't destroy yet - just nullify the reference
      // The next connect() will create a fresh instance
      if (Sortable.active) {
        this.sortable = null
      } else {
        this.sortable.destroy()
        this.sortable = null
      }
    }
  }

  /**
   * Detects if this element is a "ghost" or fallback DOM node created by SortableJS
   * during a drag operation.
   *
   * SortableJS creates temporary visual representations of dragged items. We skip
   * initializing sortable on these ephemeral nodes to avoid creating unnecessary
   * instances that would be destroyed immediately.
   *
   * @returns {boolean} True if this is a ghost/fallback node, false otherwise
   */
  get isGhost() {
    return this.element.classList.contains('sortable-ghost') ||
           this.element.classList.contains('sortable-fallback') ||
           this.element.closest('.sortable-ghost, .sortable-fallback')
  }

  /**
   * Creates and initializes a new SortableJS instance for this element.
   *
   * Checks for existing instances and element connectivity to prevent double-initialization.
   * Uses simulated drag-and-drop (forceFallback) for better stability with nested lists,
   * as it provides more predictable behavior when DOM nodes are removed during dragging.
   *
   * @throws Logs errors to console if initialization fails, but doesn't throw
   */
  initializeSortable() {
    if (this.sortable || !this.element.isConnected) return

    try {
      this.sortable = Sortable.create(this.element, {
        group: this.groupValue || null,
        animation: this.animationValue,
        handle: this.handleValue,
        ghostClass: "sortable-ghost",
        dragoverBubble: false,
        forceFallback: true, // Simulated drag-and-drop provides better stability for nested lists
        fallbackOnBody: true,
        swapThreshold: 0.65,
        onEnd: this.saveOrder.bind(this)
      })
    } catch (error) {
      console.error('Failed to initialize sortable:', error)
    }
  }

  /**
   * Callback invoked when a drag operation completes (SortableJS onEnd event).
   *
   * Collects the new order of items and persists it via PATCH request to the server.
   * Only acts if the item actually moved (oldIndex !== newIndex).
   *
   * @param {SortableEvent} event - The SortableJS event object containing oldIndex, newIndex, etc.
   * @param {number} event.oldIndex - The previous position of the dragged item
   * @param {number} event.newIndex - The new position of the dragged item
   */
  async saveOrder(event) {
    const { oldIndex, newIndex } = event
    if (oldIndex === newIndex) return

    const ids = Array.from(this.element.querySelectorAll(':scope > [data-sortable-id]'))
      .map(el => el.dataset.sortableId)

    const formData = new FormData()
    ids.forEach(id => formData.append('ids[]', id))

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        },
        body: formData
      })

      // Always render turbo streams if present (errors or successes)
      await this.renderStreamIfPresent(response)

      if (!response.ok) {
        console.error('Failed to save order:', response.status)
      }
    } catch (error) {
      console.error('Failed to save order:', error)
    }
  }

  async renderStreamIfPresent(response) {
    try {
      const contentType = response.headers.get('content-type') || ''
      const body = await response.text()

      if (contentType.includes('turbo-stream') || body.includes('<turbo-stream')) {
        Turbo.renderStreamMessage(body)
      }
    } catch (error) {
      console.error('Failed to render turbo stream:', error)
    }
  }
}
